/**
 * Teams (TEAMS_FEATURE, `/api/teams/`): named, practice-wide groups of staff that can be
 * @mentioned or assigned work as one. Work sent to a team is "Unclaimed" until a member takes it.
 */

export type TeamAlertMode = "screen" | "bell" | "bell_phone";

/** How members are told about work sent to the team. The Home screen always shows it. */
export const TEAM_ALERT_MODE_OPTIONS: {
  value: TeamAlertMode;
  label: string;
  description: string;
}[] = [
  {
    value: "screen",
    label: "Home screen only",
    description:
      "Work sent to the team appears on each member's Home screen and task list with a count. Nothing rings. Checking the list is the team's job."
  },
  {
    value: "bell",
    label: "Home screen and bell",
    description:
      "Each member also gets a bell notification in Suno for every item. Once someone takes it, everyone else's bell item is marked read."
  },
  {
    value: "bell_phone",
    label: "Home screen, bell and phone",
    description:
      "Each member with the Suno mobile app also gets a phone alert. For small teams that must react within minutes."
  }
];

/**
 * The setting new teams start on, marked "(recommended)" in the form. The bell, not "Home screen only": a member
 * who never opens Home would otherwise not notice new team work. Phones stay quiet. Must match the backend
 * default (`accounts.Team.alert_mode`, migration 0514).
 */
export const RECOMMENDED_TEAM_ALERT_MODE: TeamAlertMode = "bell";

export const TEAM_ALERT_MODE_LABELS: Record<TeamAlertMode, string> = {
  screen: "Home screen only",
  bell: "Home screen and bell",
  bell_phone: "Home screen, bell and phone"
};

/** The colours a team may pick; each is readable on white at 14px. */
export const TEAM_COLORS = [
  "#185FA5",
  "#047857",
  "#762FBD",
  "#B7791F",
  "#B91C1C",
  "#0CACA3",
  "#505F7A"
] as const;

export const DEFAULT_TEAM_COLOR = TEAM_COLORS[0];

export interface TeamMember {
  user: number;
  is_lead: boolean;
  name: string;
  role: string;
  clinic_names: string[];
  is_active: boolean;
}

export interface Team {
  id: number;
  name: string;
  color: string;
  alert_mode: TeamAlertMode;
  is_active: boolean;
  members: TeamMember[];
  member_count: number;
  /** Open tasks sent to the team that nobody has taken. */
  unclaimed_count: number;
  /** Open tasks sent to the team that a member has taken. */
  in_progress_count: number;
  created_at: string;
  updated_at: string;
}

export interface TeamMemberInput {
  user: number;
  is_lead?: boolean;
}

export interface TeamInput {
  name: string;
  color?: string;
  alert_mode?: TeamAlertMode;
  is_active?: boolean;
  /** Replaces the whole membership. */
  members?: TeamMemberInput[];
}

export interface TeamsQueryParams {
  search?: string;
  is_active?: boolean;
  /** Teams this person is on. */
  member?: number;
  limit?: number;
  offset?: number;
}

export interface TeamsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Team[];
}
